/**
 * Client for the server-held workstation lock.
 *
 * The lock lives in admin-api, not in this page. That is what stops a reload from clearing it:
 * on load the gate asks the server whether this workstation is locked, and the console's own data
 * routes answer 423 while it is, so a second tab or a direct request gets no further either.
 *
 * Note the asymmetry, which mirrors the server: reporting a lock is a plain assertion, because
 * only the browser can see the camera. Clearing one is not — it submits a frame and the server
 * decides. This client cannot unlock anything by itself, which is the point.
 */

import { adminConsoleClient } from '../../../api/adminConsoleClient';
import type { LockReason } from '../core/index.js';

const LOCK_BASE = '/work-focus/lock';

export interface RemoteLock {
  locked: boolean;
  reason: LockReason | null;
  evidence: string[];
  /** False when the server could not be reached, so `locked: false` must not be trusted. */
  known: boolean;
}

function asReason(value: unknown): LockReason | null {
  return value === 'face_absent' ||
    value === 'identity_mismatch' ||
    value === 'camera_unavailable' ||
    value === 'console_backgrounded'
    ? value
    : null;
}

/** Ask the server whether this workstation is locked. Called on every page load. */
export async function fetchLock(): Promise<RemoteLock> {
  try {
    const { data } = await adminConsoleClient.get<Record<string, unknown>>(LOCK_BASE);
    return {
      locked: data.locked === true,
      reason: asReason(data.reason),
      evidence: Array.isArray(data.evidence) ? data.evidence.map(String) : [],
      known: true,
    };
  } catch {
    // Distinguished from a genuine "not locked": an unreachable server must not read as an
    // all-clear, or an outage would silently disable the control on every workstation.
    return { locked: false, reason: null, evidence: [], known: false };
  }
}

/**
 * Tell the server this workstation has locked.
 *
 * Returns whether the report was accepted. A failure is worth knowing about: the overlay will
 * still be showing locally, but the lock would not survive a reload, so the caller can retry.
 */
export async function reportLock(
  reason: LockReason,
  evidence: readonly string[],
): Promise<boolean> {
  try {
    await adminConsoleClient.post(LOCK_BASE, { reason, evidence: [...evidence] });
    return true;
  } catch {
    return false;
  }
}

export interface ClearOutcome {
  /** True only when the server actually released the lock. */
  cleared: boolean;
  /** True when the release was backed by a face match rather than an unverifiable fallback. */
  verified: boolean;
  similarity: number | null;
  detail: string;
}

async function toBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/**
 * Submit the captured frames to clear the lock.
 *
 * The verdict is entirely the server's. A refusal (403) and a retryable "no usable face" (422)
 * both come back as `cleared: false` with the server's own explanation, so the overlay can tell
 * the operator which happened.
 */
export async function requestClear(frames: readonly Blob[]): Promise<ClearOutcome> {
  const images = await Promise.all(frames.map((frame) => toBase64(frame)));

  try {
    const { data } = await adminConsoleClient.post<Record<string, unknown>>(`${LOCK_BASE}/clear`, {
      images,
    });
    return {
      cleared: data.locked !== true,
      verified: data.verified === true,
      similarity: typeof data.similarity === 'number' ? data.similarity : null,
      detail: typeof data.detail === 'string' ? data.detail : '',
    };
  } catch (error) {
    const response = (error as { response?: { data?: Record<string, unknown> } }).response;
    const data = response?.data ?? {};
    return {
      cleared: false,
      verified: false,
      similarity: typeof data.similarity === 'number' ? data.similarity : null,
      detail:
        typeof data.detail === 'string' && data.detail.length > 0
          ? data.detail
          : 'the workstation could not be unlocked',
    };
  }
}
